import { isNil } from 'es-toolkit/compat';
import tlds from 'tlds';
import isURL from 'validator/lib/isURL';

const normalizedAllowedTlds = new Set(
    tlds.map((tld) => new URL(`https://example.${tld}`).hostname.split('.').at(-1)).filter((tld): tld is string => !!tld)
);

const allowedUrlProtocols = ['http:', 'https:'] as const;
const restaurantUrlValidationOptions = {
    protocols: ['http', 'https'],
    require_protocol: true,
    require_valid_protocol: true,
    require_host: true,
    require_tld: true,
    allow_query_components: true,
    allow_fragments: true,
    allow_protocol_relative_urls: false,
    allow_underscores: false,
    allow_trailing_dot: false,
    disallow_auth: true,
    validate_length: true,
} as const;

export const cleanUtmParamsFromUrl = (url: string): string => {
    const splitUrl = url.split('?');
    let cleanedUrl = splitUrl[0];

    if (splitUrl.length > 1) {
        const urlParamsString = splitUrl[1];
        const urlParams = new URLSearchParams(urlParamsString);
        const keys = urlParams.keys();
        const keysToDelete: string[] = [];
        let key = keys.next();
        while (!key.done) {
            if (key.value.includes('utm_')) {
                keysToDelete.push(key.value);
            }
            key = keys.next();
        }
        keysToDelete.forEach((keyToDelete) => urlParams.delete(keyToDelete));

        const cleanUrlParams = urlParams.toString();
        if (cleanUrlParams.length > 0) {
            cleanedUrl += `?${urlParams.toString()}`;
        }
    }

    return cleanedUrl;
};

export const includesUtmParams = (url: string): boolean => {
    try {
        const urlObj = new URL(url);
        const urlParams = new URLSearchParams(urlObj.search);
        const keys = urlParams.keys();
        let key = keys.next();
        while (!key.done) {
            if (key.value.includes('utm_')) {
                return true;
            }
            key = keys.next();
        }
        return false;
    } catch {
        return false;
    }
};

export function cleanUrl(url) {
    if (isNil(url)) {
        return null;
    }
    return cleanUtmParamsFromUrl(url);
}

/**
 * Checks if a given string is a valid URL and has a valid TLD.
 */
export function isValidUrl(url: string, options?: { allowEmpty: boolean }): boolean {
    if (options?.allowEmpty && url === '') {
        return true;
    }

    if (!isURL(url, restaurantUrlValidationOptions)) {
        return false;
    }

    let urlObj: URL;
    try {
        urlObj = new URL(url);
    } catch {
        return false;
    }

    if (!allowedUrlProtocols.includes(urlObj.protocol as (typeof allowedUrlProtocols)[number])) {
        return false;
    }

    const asciiHostname = urlObj.hostname;

    if (asciiHostname.length === 0) {
        return false;
    }

    const domainParts = asciiHostname.split('.');

    if (domainParts.length < 2 || domainParts.some((domainPart) => domainPart.length === 0)) {
        return false;
    }

    const asciiTld = domainParts.at(-1);

    if (!asciiTld) {
        return false;
    }

    if (!normalizedAllowedTlds.has(asciiTld)) {
        return false;
    }

    return true;
}

/**
 * Normalizes a URL so a valid-but-unencoded URL (e.g. raw spaces in a Google Maps `destination=` address) becomes percent-encoded and
 * passes {@link isValidUrl}. Uses the URL constructor: no double-encoding of `%xx`, query structure preserved. Returns null if unfixable.
 */
export function normalizeUrl(url: string): string | null {
    try {
        const normalized = new URL(url).href;
        return isValidUrl(normalized, { allowEmpty: false }) ? normalized : null;
    } catch {
        return null;
    }
}

export const changeUrlDomain = (url: string, newHostname: string) => {
    try {
        // Add http to url if it doesn't have it to prevent URL constructor
        // to throw an error
        const urlObj = url.match(/^https?:\/\//) ? new URL(url) : new URL(`http://${url}`);

        // Parse the URL to get different parts
        const { hostname } = urlObj;

        if (!hostname) {
            console.error(`Error changing domain, invalid format: ${url}`);
            return url;
        }

        // Construct the new hostname
        urlObj.hostname = newHostname;

        // Remove the protocol if it was initially absent
        const resultUrl = url.match(/^https?:\/\//) ? urlObj.toString() : urlObj.toString().replace('http://', '');

        return resultUrl;
    } catch (error: any) {
        console.error(`Error changing domain: ${error.message}`);
        return url;
    }
};

export const getUrlDomain = (url: string): string | undefined => {
    try {
        const urlObj = new URL(url);
        const { hostname } = urlObj;
        const domainParts = hostname.split('.');

        // Check if we have at least a domain and a TLD
        if (domainParts.length < 2) {
            return undefined;
        }

        return domainParts[domainParts.length - 2];
    } catch {
        return undefined;
    }
};

export const isFromDomain = ({ url, domain }: { url: string; domain: string }): boolean => {
    try {
        const domainToCheck = getUrlDomain(url);

        if (!domainToCheck) {
            return false;
        }

        return domainToCheck === domain;
    } catch {
        return false;
    }
};

export const hasSameDomain = (url1, url2): boolean => {
    try {
        // Get both domains
        const domainToCheck = getUrlDomain(url1);
        const domainToCheck2 = getUrlDomain(url2);

        if (!domainToCheck || !domainToCheck2) {
            return false;
        }

        return domainToCheck === domainToCheck2;
    } catch {
        return false;
    }
};

export const trimSlashes = (pathname: string): string => {
    let trimmedPathname = pathname;

    if (pathname.startsWith('/')) {
        trimmedPathname = trimmedPathname.slice(1);
    }

    if (pathname.endsWith('/')) {
        trimmedPathname = trimmedPathname.slice(0, -1);
    }

    return trimmedPathname;
};

export const containsForbiddenDomains = (url: string, forbiddenDomains: string[]): boolean => {
    try {
        const urlObj = new URL(url);
        const { hostname } = urlObj;

        return forbiddenDomains.some((forbiddenDomain) => hostname.includes(forbiddenDomain));
    } catch {
        return false;
    }
};
